import React, { useContext, useMemo } from "react";
import Title from "../../../components/Title";
import BarGraph from "../../../components/BarGraph";
import { Detalle, Entorno } from "../../../utils/constants";
import AppContext, { IFilters } from "../../../AppContext";
import { gql, useQuery } from "@apollo/client";
import {
  getStepsFixed as getSteps,
  getMinFixed as getMin,
  getMaxFixed as getMax,
} from "../../../utils/functions";
import { MessageBar, Spinner, SpinnerSize } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { t } from "i18next"; 

export const titleEn = "Market by segment";
export const title = "Mercado por segmentos";

export const filters: IFilters = {
  limitedPeriodo: false,
  showMedidas: true,
  hasTicketMedio: false,
  isDesglose: true,
  showEntorno: true,
  showSegmentos: false,
  showMyData: false,
};

const Graph = () => {
  const { semanas, filters, user } = useContext(AppContext);
  let entorno: "Comparable" | "Total" =
    filters.entorno === Entorno.Comparable ? "Comparable" : "Total";
  let isDesglosado = filters.detalle === Detalle.Desglose;

  const GET_GRAPH = gql`
    query ($yr1: Int!, $wk1: Int!, $yr2: Int!, $wk2: Int!) {
      graph(
        Graph: "Mercado por segmentos"
        WeekRange: {
          Start: { Year: $yr1, Week: $wk1 }
          End: { Year: $yr2, Week: $wk2 }
        }
      )
    }
  `;

  const qry = useQuery(GET_GRAPH, {
    fetchPolicy: "no-cache",
    variables: {
      yr1: semanas[0].year,
      wk1: semanas[0].week,
      yr2: semanas[1].year,
      wk2: semanas[1].week,
    },
  });

  const { t } = useTranslation();

  const translateLabel = (label: string) => {
    const norm = (label || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (norm === 'cafe') return t('Cafe') as string;
    if (norm === 'mercado') return t('Market') as string;
    return t(label) !== label ? t(label) as string : label;
  };

  let errorMsg = t('An-error-has-occur-when-recovering-data');
  let loading = qry.loading || qry.data === undefined;
  let error = !semanas || !!qry.error;
  let labels: string[] = [],
    values: any = [],
    values_total: any = useMemo(() => [], []),
    values_desglose: any = useMemo(() => [], []);
  //let annotation:any=[]

  let data: any;
  let exportData: any;
  if (!loading && !error && qry.data.graph) {
    try {
      data = qry?.data?.graph;

      if (!data) throw new Error(errorMsg);
      if (data?.Error) throw new Error(`${data.Error}`);

      if (!data?.Error) {
        labels = data.map((d: any) => Object.keys(d)[0]);
        values_total = [
          data.map((d: any[]) => Object.values(d)[0][entorno]["Ventas"]),
        ];
        values_desglose = [
          data.map((d: any[]) => Object.values(d)[0][entorno]["Tickets"]),
          data.map((d: any[]) => Object.values(d)[0][entorno]["TicketMedio"]),
        ];

        values = isDesglosado ? values_desglose : values_total;
      }
      exportData =
        semanas[0].year === 2019
          ? getExportData(labels, [...values_total, ...values_desglose], translateLabel, t)
          : data;
    } catch (err: any) {
      error = true;
      errorMsg = err.message ?? errorMsg;
    }
  }

  let max: number | undefined = useMemo(
    () => getMax([...values_desglose, ...values_total]),
    [values_total, values_desglose]
  );
  let min: number | undefined = useMemo(
    () => getMin([...values_desglose, ...values_total]),
    [values_total, values_desglose]
  );

  const stepSize = useMemo(() => getSteps(min, max), [min, max]);

  // round max to next integer
  max = Math.ceil(max + 1);
  // round min to previous integer
  min = Math.floor(min);
  const graphData = {
    datalabels: isDesglosado ? [t('Tickets'), t('Average-ticket')] : [t('Sales')],
    labels: labels.map(lab => translateLabel(lab)),
    values: values,
    colors: isDesglosado ? ["#0C4D7B", "#85b709"] : ["#0e6773"],
    annotation: [],
    series: isDesglosado
      ? [
          { name: t('Tickets'), data: values[0] ?? [] },
          { name: t('Average-ticket'), data: values[1] ?? [] },
        ]
      : [{ name: t('Sales'), data: values[0] }],
    min: min,
    max: max,
    stepSize: stepSize,
  };

  return (
    <>
      <Title
        title={t('Market-by-segments')}
        error={!!error}
        files={{
          data: exportData,
          semanas,
          user,
          title: t('Market-by-segments'),
          leyenda: t('Scope'),
          transposed: true,
        }}
      />
      {error && <MessageBar isMultiline={true}>{errorMsg}</MessageBar>}
      {loading && (
        <div className="loading-graph">
          <Spinner size={SpinnerSize.large} />
        </div>
      )}
      {!error && <BarGraph data={graphData} percentages />}
    </>
  );
};

export default Graph;


const getExportData = (labels: string[], values: any[], translateLabel: (l: string) => string, t: Function) => {
  let exportData: any = [];

  for (let label in labels) {
    exportData.push({
      [translateLabel(labels[label])]: {
        [t('Total')]: {
          [t('Sales')]: values[0][label],
          [t('Tickets')]: values[1][label],
          [t('Average-ticket')]: values[2][label],
        },
      },
    });
  }

  return exportData;
};
